import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  FilmIcon,
  EyeIcon,
  DocumentIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import DataTable from '@/components/common/DataTable';
import Badge from '@/components/common/Badge';
import Modal from '@/components/common/Modal';
import type {
  HowToVideo,
  HowToVideoCategory,
  HowToVideoStatus,
  HowToVideoStats,
} from '@/types/howToVideo';
import {
  getHowToVideos,
  createHowToVideo,
  updateHowToVideo,
  deleteHowToVideo,
  uploadHowToVideoFile,
  uploadHowToThumbnail,
} from '@/services/howToVideoApi';

const STATUS_BADGE: Record<HowToVideoStatus, { variant: 'success' | 'warning' | 'gray'; label: string }> = {
  published: { variant: 'success', label: 'Published' },
  draft: { variant: 'warning', label: 'Draft' },
  archived: { variant: 'gray', label: 'Archived' },
};

const CATEGORY_BADGE: Record<HowToVideoCategory, { variant: 'info' | 'primary'; label: string }> = {
  consumer: { variant: 'info', label: 'Consumer' },
  business: { variant: 'primary', label: 'Business' },
};

const PAGE_SIZE = 20;

function formatDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function HowToVideos() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState<HowToVideo | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCategory, setFormCategory] = useState<HowToVideoCategory>('consumer');
  const [formFunctionalityName, setFormFunctionalityName] = useState('');
  const [formScreenName, setFormScreenName] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formVideoUrl, setFormVideoUrl] = useState('');
  const [formThumbnailUrl, setFormThumbnailUrl] = useState('');
  const [formDuration, setFormDuration] = useState<number | ''>('');
  const [formDisplayOrder, setFormDisplayOrder] = useState(0);
  const [formStatus, setFormStatus] = useState<HowToVideoStatus>('draft');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const videoInputRef = useRef<HTMLInputElement>(null);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['howToVideos', page, statusFilter, categoryFilter, search],
    queryFn: () => getHowToVideos(page, PAGE_SIZE, categoryFilter, statusFilter, search),
  });

  const stats: HowToVideoStats = data?.stats || { total: 0, published: 0, draft: 0, consumer: 0, business: 0 };

  const createMutation = useMutation({
    mutationFn: createHowToVideo,
    onSuccess: () => {
      toast.success('Video created successfully');
      queryClient.invalidateQueries({ queryKey: ['howToVideos'] });
      closeModal();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: updateHowToVideo,
    onSuccess: () => {
      toast.success('Video updated successfully');
      queryClient.invalidateQueries({ queryKey: ['howToVideos'] });
      closeModal();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHowToVideo,
    onSuccess: () => {
      toast.success('Video deleted');
      queryClient.invalidateQueries({ queryKey: ['howToVideos'] });
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function openCreate() {
    setEditingVideo(null);
    resetForm();
    setShowModal(true);
  }

  function openEdit(video: HowToVideo) {
    setEditingVideo(video);
    setFormTitle(video.title);
    setFormDescription(video.description || '');
    setFormCategory(video.category);
    setFormFunctionalityName(video.functionality_name || '');
    setFormScreenName(video.screen_name || '');
    setFormTags(video.tags.join(', '));
    setFormVideoUrl(video.video_url);
    setFormThumbnailUrl(video.thumbnail_url || '');
    setFormDuration(video.duration_seconds ?? '');
    setFormDisplayOrder(video.display_order);
    setFormStatus(video.status);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingVideo(null);
    resetForm();
  }

  function resetForm() {
    setFormTitle('');
    setFormDescription('');
    setFormCategory('consumer');
    setFormFunctionalityName('');
    setFormScreenName('');
    setFormTags('');
    setFormVideoUrl('');
    setFormThumbnailUrl('');
    setFormDuration('');
    setFormDisplayOrder(0);
    setFormStatus('draft');
    setIsUploading(false);
    setUploadProgress('');
  }

  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setUploadProgress('Uploading video...');
    try {
      const url = await uploadHowToVideoFile(file);
      setFormVideoUrl(url);

      // Try to get duration from video element
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = url;
      video.onloadedmetadata = () => {
        setFormDuration(Math.round(video.duration));
        URL.revokeObjectURL(video.src);
      };

      toast.success('Video uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  }

  async function handleThumbnailUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setUploadProgress('Uploading thumbnail...');
    try {
      const url = await uploadHowToThumbnail(file);
      setFormThumbnailUrl(url);
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  }

  function handleSubmit() {
    if (!formTitle.trim() || !formVideoUrl.trim()) {
      toast.error('Title and video are required');
      return;
    }

    const tags = formTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const payload = {
      title: formTitle.trim(),
      description: formDescription.trim() || undefined,
      category: formCategory,
      tags,
      screen_name: formScreenName.trim() || undefined,
      functionality_name: formFunctionalityName.trim() || undefined,
      video_url: formVideoUrl.trim(),
      thumbnail_url: formThumbnailUrl.trim() || undefined,
      duration_seconds: formDuration ? Number(formDuration) : undefined,
      display_order: formDisplayOrder,
      status: formStatus,
    };

    if (editingVideo) {
      updateMutation.mutate({ id: editingVideo.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const columns = useMemo<ColumnDef<HowToVideo>[]>(
    () => [
      {
        id: 'thumbnail',
        header: '',
        size: 80,
        cell: ({ row }) => (
          <div className="w-16 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
            {row.original.thumbnail_url ? (
              <img
                src={row.original.thumbnail_url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <FilmIcon className="w-5 h-5 text-gray-400" />
            )}
          </div>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900">{row.original.title}</p>
            {row.original.functionality_name && (
              <p className="text-xs text-gray-500">{row.original.functionality_name}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => {
          const cfg = CATEGORY_BADGE[row.original.category];
          return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const cfg = STATUS_BADGE[row.original.status];
          return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
        },
      },
      {
        accessorKey: 'duration_seconds',
        header: 'Duration',
        cell: ({ row }) => (
          <span className="text-gray-600 text-sm">
            {formatDuration(row.original.duration_seconds)}
          </span>
        ),
      },
      {
        accessorKey: 'display_order',
        header: 'Order',
        cell: ({ row }) => (
          <span className="text-gray-600 text-sm">{row.original.display_order}</span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-gray-500 text-sm">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openEdit(row.original);
              }}
              className="p-1.5 text-gray-400 hover:text-brand rounded-lg hover:bg-gray-100 transition-colors"
              title="Edit"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(row.original.id);
              }}
              className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">How-To Videos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage tutorial videos for consumer and business users
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand/90 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Video
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, icon: FilmIcon, color: 'text-gray-600' },
          { label: 'Published', value: stats.published, icon: EyeIcon, color: 'text-green-600' },
          { label: 'Draft', value: stats.draft, icon: DocumentIcon, color: 'text-yellow-600' },
          { label: 'Consumer', value: stats.consumer, icon: UserGroupIcon, color: 'text-blue-600' },
          { label: 'Business', value: stats.business, icon: BuildingStorefrontIcon, color: 'text-purple-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <Icon className={`w-5 h-5 ${color}`} />
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-xl font-bold text-gray-900">{value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search videos..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none w-64"
        />
        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
        >
          <option value="all">All Categories</option>
          <option value="consumer">Consumer</option>
          <option value="business">Business</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
        >
          <option value="all">All Statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <DataTable
        data={data?.videos || []}
        columns={columns}
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={data?.total || 0}
        onPageChange={setPage}
        onRowClick={(video) => openEdit(video)}
        isLoading={isLoading}
      />

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title={editingVideo ? 'Edit Video' : 'Add Video'} size="xl">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="e.g., How to Send a Message"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={3}
              placeholder="Describe what this video covers..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none resize-none"
            />
          </div>

          {/* Category + Status row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value as HowToVideoCategory)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              >
                <option value="consumer">Consumer</option>
                <option value="business">Business</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value as HowToVideoStatus)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Functionality Name + Screen Name row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Functionality Name</label>
              <input
                type="text"
                value={formFunctionalityName}
                onChange={(e) => setFormFunctionalityName(e.target.value)}
                placeholder="e.g., Send a Message"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Screen Name</label>
              <input
                type="text"
                value={formScreenName}
                onChange={(e) => setFormScreenName(e.target.value)}
                placeholder="e.g., ConversationScreen"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={formTags}
              onChange={(e) => setFormTags(e.target.value)}
              placeholder="e.g., messaging, chat, contacts"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            />
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Video *</label>
            {formVideoUrl ? (
              <div className="space-y-2">
                <video
                  src={formVideoUrl}
                  controls
                  className="w-full max-h-48 rounded-lg bg-black"
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 truncate flex-1">{formVideoUrl}</span>
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    className="text-xs text-brand hover:underline"
                  >
                    Replace
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={isUploading}
                className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-brand hover:text-brand transition-colors text-sm"
              >
                {isUploading ? uploadProgress : 'Click to upload video (MP4, up to 100MB)'}
              </button>
            )}
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/quicktime"
              onChange={handleVideoUpload}
              className="hidden"
            />
          </div>

          {/* Thumbnail Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail</label>
            {formThumbnailUrl ? (
              <div className="space-y-2">
                <img
                  src={formThumbnailUrl}
                  alt="Thumbnail"
                  className="w-32 h-20 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => thumbInputRef.current?.click()}
                  className="text-xs text-brand hover:underline"
                >
                  Replace thumbnail
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => thumbInputRef.current?.click()}
                disabled={isUploading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:border-brand text-sm transition-colors"
              >
                {isUploading ? uploadProgress : 'Upload thumbnail image'}
              </button>
            )}
            <input
              ref={thumbInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleThumbnailUpload}
              className="hidden"
            />
          </div>

          {/* Duration + Display Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (seconds)</label>
              <input
                type="number"
                value={formDuration}
                onChange={(e) => setFormDuration(e.target.value ? Number(e.target.value) : '')}
                placeholder="Auto-detected on upload"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input
                type="number"
                value={formDisplayOrder}
                onChange={(e) => setFormDisplayOrder(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : editingVideo ? 'Update' : 'Create'}
          </button>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={!!deleteId} onClose={() => setDeleteId(null)} title="Delete Video" size="sm">
        <p className="text-sm text-gray-600">
          Are you sure you want to delete this video? This action cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={() => setDeleteId(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
